/**
 * LLM-assisted criteria recommendation
 *
 * Registry: criteria_recommendation — embedded advisor. Transport: callFeatureLlm.
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const CRITERIA_SYSTEM_PROMPT =
  'You recommend which scoring criteria attributes best differentiate a set of prioritization items. Respond with valid JSON only.';

export interface AttributeSummary {
  key: string;
  label: string;
  description?: string;
}

export interface ItemSummary {
  id: string;
  title: string;
  description?: string;
}

export interface CriteriaRecommendation {
  attributeKey: string;
  attributeLabel: string;
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface RecommendCriteriaResult {
  recommendations: CriteriaRecommendation[];
  /** Overall reasoning the LLM used */
  reasoning?: string;
}

/**
 * Recommend which attributes to promote to scoring criteria given the item pool.
 * Returns up to 5 ranked recommendations with rationale.
 */
export async function recommendCriteria(
  items: ItemSummary[],
  attributes: AttributeSummary[],
  toolId?: string,
): Promise<RecommendCriteriaResult> {
  if (!isAiCapabilityEnabled('criteria_recommendation') || items.length === 0 || attributes.length === 0) {
    return { recommendations: [] };
  }

  try {
    const content = await callFeatureLlm({
      userPrompt: buildRecommendationPrompt(items, attributes, toolId),
      systemPrompt: CRITERIA_SYSTEM_PROMPT,
      conversationId: 'criteria-recommend-' + Date.now(),
      model: DEFAULT_MODEL,
      temperature: 0.3,
      maxTokens: 600,
    });
    if (!content) return { recommendations: [] };
    return parseRecommendationResponse(content, attributes);
  } catch (error) {
    console.warn('LLM criteria recommendation failed:', error);
    return { recommendations: [] };
  }
}

function buildRecommendationPrompt(
  items: ItemSummary[],
  attributes: AttributeSummary[],
  toolId?: string
): string {
  const itemList = items
    .slice(0, 30)
    .map(
      (i, idx) =>
        `[${idx + 1}] ${i.title}${i.description ? `: ${i.description.slice(0, 120)}` : ''}`
    )
    .join('\n');

  const attrList = attributes
    .map(
      (a) =>
        `- key="${a.key}" label="${a.label}"${a.description ? ` (${a.description.slice(0, 80)})` : ''}`
    )
    .join('\n');

  const toolHint = toolId
    ? `\nThe team is using the "${toolId}" prioritization method.`
    : '';

  return `You are a prioritization facilitator helping a team choose scoring criteria.${toolHint}

The team has ${items.length} candidate items to prioritize. Here is a sample of them:
${itemList}

Available attributes they can use as scoring criteria:
${attrList}

Your task: Recommend which 3–5 attributes from the list above would best differentiate these items and help the team make a high-quality prioritization decision. Consider:
- Which attributes are likely to vary meaningfully across these specific items
- Which attributes are most relevant to the nature of the work (features vs. risks vs. investments, etc.)
- Avoid recommending attributes that are likely the same for all items (low differentiation)

Return JSON only, no markdown or extra text:
{
  "recommendations": [
    {
      "attributeKey": "exact key from the list",
      "attributeLabel": "label from the list",
      "rationale": "1-2 sentence explanation of why this attribute differentiates these items",
      "confidence": "high" | "medium" | "low"
    }
  ],
  "reasoning": "One sentence on your overall approach"
}

Include only attributes from the provided list. Order by how strongly you recommend them.`;
}

function parseRecommendationResponse(
  content: string,
  attributes: AttributeSummary[]
): RecommendCriteriaResult {
  const validKeys = new Set(attributes.map((a) => a.key));

  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      const rawRecs = Array.isArray(parsed.recommendations) ? parsed.recommendations : [];
      const recommendations: CriteriaRecommendation[] = rawRecs
        .filter(
          (r: unknown) =>
            r &&
            typeof r === 'object' &&
            typeof (r as { attributeKey?: unknown }).attributeKey === 'string' &&
            validKeys.has((r as { attributeKey: string }).attributeKey)
        )
        .slice(0, 5)
        .map((r: Record<string, unknown>) => {
          const attr = attributes.find((a) => a.key === r.attributeKey);
          return {
            attributeKey: r.attributeKey as string,
            attributeLabel: (r.attributeLabel as string) ?? attr?.label ?? (r.attributeKey as string),
            rationale: typeof r.rationale === 'string' ? r.rationale.trim() : '',
            confidence: validConfidence(r.confidence as string),
          };
        });

      return {
        recommendations,
        reasoning: typeof parsed.reasoning === 'string' ? parsed.reasoning.trim() : undefined,
      };
    }
  } catch (e) {
    console.warn('Parse criteria recommendation response failed:', e);
  }

  return { recommendations: [] };
}

function validConfidence(s: string): 'high' | 'medium' | 'low' {
  return s === 'high' || s === 'low' ? s : 'medium';
}
